import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, startCheckout } from "@/lib/api";
import { toast } from "sonner";

/**
 * Shared swing analysis paywall logic used by both brands.
 * - Fetches usage for the entered email
 * - Detects ?paid=session_id from URL (user returning from Stripe success page)
 * - Exposes AI + human checkout starters
 */
export function useSwingPaywall(email, brand) {
    const [usage, setUsage] = useState({ used: 0, free_limit: 5, requires_payment: false, ai_price: 3, human_price: 39 });
    const [loading, setLoading] = useState(false);
    const [params] = useSearchParams();
    const paidSessionId = params.get("paid") || "";

    const refresh = useCallback(async (e) => {
        const em = (e ?? email ?? "").trim().toLowerCase();
        if (!em) {
            setUsage({ used: 0, free_limit: 5, requires_payment: false, ai_price: 3, human_price: 39 });
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.get(`/swing/usage?email=${encodeURIComponent(em)}`);
            setUsage(data);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, [email]);

    useEffect(() => { refresh(email); }, [email, refresh]);

    const payForAi = async () => {
        if (!email) return toast.error("Enter your email first — we track your free credits by email.");
        try {
            await startCheckout({ item_type: "swing_ai", brand, customer_email: email });
        } catch (err) { toast.error(err?.response?.data?.detail || "Could not start checkout"); }
    };
    const payForHuman = async () => {
        if (!email) return toast.error("Enter your email first.");
        try {
            await startCheckout({ item_type: "swing_human", brand, customer_email: email });
        } catch (err) { toast.error(err?.response?.data?.detail || "Could not start checkout"); }
    };

    return { usage, loading, refresh, payForAi, payForHuman, paidSessionId };
}
